import React from "react";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import PropTypes from "prop-types";
import "./styles/CommonButton.css";

/*
  CommonButton — ZERO inline styles.
  All colours/shapes come from CommonButton.css via CSS variables,
  so every theme in themes.json works automatically.

  Variant → CSS class mapping:
    primary          → common-button--primary
    blue             → common-button--blue
    success          → common-button--success
    purple           → common-button--purple
    danger           → common-button--danger
    warning          → common-button--warning
    info             → common-button--info
    secondary        → common-button--secondary
    outline-primary  → common-button--outline-primary
    outline-secondary→ common-button--outline-secondary
    tab              → common-button--tab  (+--selected when isSelected)
    smallAction      → common-button--smallaction
    arrow            → common-button--arrow
    custom           → (no variant class — caller adds className)
*/

const CommonButton = ({
  label,
  title,
  onClick,
  disabled  = false,
  symbol    = false,
  variant   = "primary",
  className = "",
  loading   = false,
  isSelected = false,
  size      = "default",
  bold      = false,
  // legacy props kept for back-compat but ignored (CSS handles colours)
  hoverBoxShadow,
  defaultBoxShadow,
}) => {

  /* Build the class string — no style= at all */
  const variantKey = variant.toLowerCase().replace(/_/g, "");
  const sizeClass  = size === "small" ? "common-button--sm"
                   : size === "large" ? "common-button--lg"
                   : "";

  const classes = [
    "common-button",
    `common-button--${variantKey}`,
    isSelected && variant === "tab" ? "common-button--selected" : "",
    loading   ? "common-button--loading" : "",
    bold      ? "common-button--bold"    : "",
    sizeClass,
    className,
  ]
    .filter(Boolean)
    .join(" ");

  /* Content */
  const renderContent = () => {
    if (loading) {
      return (
        <>
          <span className="common-button__spinner" aria-hidden="true" />
          {label}
        </>
      );
    }
    return (
      <>
        {symbol && (
          <FontAwesomeIcon
            icon={symbol}
            style={{
              fontSize: size === "small" ? "10px" : "12px",
              marginRight: label ? "4px" : "0",
            }}
          />
        )}
        {label}
      </>
    );
  };

  return (
    <button
      onClick={onClick}
      title={title}
      disabled={disabled || loading}
      className={classes}
    >
      {renderContent()}
    </button>
  );
};

CommonButton.propTypes = {
  label:            PropTypes.string,
  title:            PropTypes.string.isRequired,
  onClick:          PropTypes.func.isRequired,
  disabled:         PropTypes.bool,
  symbol:           PropTypes.any,
  variant:          PropTypes.oneOf([
    "primary", "blue", "success", "purple", "danger", "warning",
    "info", "secondary", "outline-primary", "outline-secondary",
    "tab", "smallAction", "arrow", "custom",
  ]),
  className:        PropTypes.string,
  loading:          PropTypes.bool,
  isSelected:       PropTypes.bool,
  size:             PropTypes.oneOf(["default", "small", "large"]),
  bold:             PropTypes.bool,
  hoverBoxShadow:   PropTypes.string,   // ignored — kept for compat
  defaultBoxShadow: PropTypes.string,   // ignored — kept for compat
};

CommonButton.defaultProps = {
  variant:   "primary",
  disabled:  false,
  loading:   false,
  className: "",
  isSelected: false,
  size:      "default",
  bold:      false,
};

export default CommonButton;